import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Node, useNodes, useReactFlow, useUpdateNodeInternals } from '@xyflow/react';

import { NodeDimensions } from '../types/graph';

import { useForLoopData } from './useForLoopData';
import { useNodeValidation } from './useNodeValidation';
import { useThrottledResize } from './useThrottledResize';

export const useDynamicForLoopSizing = (nodeId: string) => {
  const { setNodes } = useReactFlow();
  const nodes = useNodes();
  const updateNodeInternals = useUpdateNodeInternals();

  // Leverage existing ForLoop data management
  const { getForLoopData } = useForLoopData({
    forLoopNodeId: nodeId,
    setNodes,
  });

  // Integrate with existing validation system
  const { validateNode } = useNodeValidation();

  // Helper to check validation errors for a node
  const hasValidationErrors = useCallback(
    (id: string): boolean => {
      const node = nodes.find(n => n.id === id);
      if (!node) return false;
      const result = validateNode(node);
      return !result.isValid;
    },
    [nodes, validateNode]
  );

  // Get initial dimensions from the actual React Flow node (from saved metadata)
  const currentNode = useMemo(() => nodes.find(n => n.id === nodeId), [nodes, nodeId]);
  const initialDimensions = useMemo(() => {
    if (currentNode?.width && currentNode?.height) {
      return { width: currentNode.width, height: currentNode.height };
    }
    return { width: 320, height: 188 };
  }, [currentNode]);

  // Use throttling for performance
  const { dimensions, scheduleUpdate } = useThrottledResize(16, initialDimensions);

  // Track previous dimensions to prevent unnecessary updates
  const previousDimensionsRef = useRef<NodeDimensions | null>(null);

  // Get node dimensions based on node type
  const getNodeDimensions = useCallback((node: Node): { width: number; height: number } => {
    // Default dimensions for different node types
    const nodeTypeDimensions: Record<string, { width: number; height: number }> = {
      specialized_agent: { width: 248, height: 108 },
      conditional_step: { width: 248, height: 108 },
      condition: { width: 248, height: 108 },
      for_loop: { width: 320, height: 188 },
      nested_workflow: { width: 248, height: 108 },
      webhook_trigger: { width: 248, height: 108 },
      scheduled_trigger: { width: 248, height: 108 },
      message_trigger: { width: 248, height: 108 },
      email_trigger: { width: 248, height: 108 },
    };

    // Use explicit dimensions if available, otherwise fall back to node type defaults
    const explicitWidth = node.width || node.style?.width;
    const explicitHeight = node.height || node.style?.height;

    // Get dimensions based on node type
    const nodeType = node.type || 'specialized_agent';
    const defaultDims = nodeTypeDimensions[nodeType] || { width: 248, height: 108 };

    const parseSize = (size: string | number | undefined, fallback: number): number => {
      if (!size) return fallback;
      return typeof size === 'string' ? parseInt(size) : size;
    };

    return {
      width: parseSize(explicitWidth, defaultDims.width),
      height: parseSize(explicitHeight, defaultDims.height),
    };
  }, []);

  // Calculate dimensions based on child nodes of ALL types
  const calculateDimensions = useCallback(
    (childNodes: Node[]): NodeDimensions => {
      try {
        const forLoopData = getForLoopData();
        if (!forLoopData) return { width: 320, height: 188 };

        // Don't resize if there are validation errors
        const currentNode = nodes.find(n => n.id === nodeId);
        if (currentNode && hasValidationErrors(nodeId)) {
          return { width: currentNode.width || 320, height: currentNode.height || 188 };
        }

        // If no children, return minimum size
        if (childNodes.length === 0) {
          return { width: 320, height: 188 };
        }

        // Calculate based on child positions with their actual dimensions
        const bounds = childNodes.reduce(
          (acc, child) => {
            const childDims = getNodeDimensions(child);
            return {
              maxX: Math.max(acc.maxX, child.position.x + childDims.width),
              maxY: Math.max(acc.maxY, child.position.y + childDims.height),
            };
          },
          { maxX: 0, maxY: 0 }
        );

        // Add padding and ensure minimum sizes
        const padding = 80;
        return {
          width: Math.max(bounds.maxX + padding, 320),
          height: Math.max(bounds.maxY + padding, 188),
        };
      } catch (error) {
        console.warn('Error calculating dimensions:', error);
        return { width: 320, height: 188 };
      }
    },
    [getForLoopData, hasValidationErrors, nodeId, nodes, getNodeDimensions]
  );

  // Monitor child nodes using existing patterns - optimized filtering
  const childNodes = useMemo(() => {
    const children: Node[] = [];
    for (const node of nodes) {
      if (node.parentId === nodeId) {
        children.push(node);
      }
    }
    return children;
  }, [nodes, nodeId]);

  // Update dimensions when children change
  useEffect(() => {
    const newDimensions = calculateDimensions(childNodes);
    scheduleUpdate(newDimensions);
  }, [childNodes, calculateDimensions, scheduleUpdate]);

  // Ensure dimensions are properly initialized on mount
  useEffect(() => {
    if (!dimensions && initialDimensions) {
      // If we have initial dimensions but no calculated dimensions yet,
      // validate that the initial dimensions are still appropriate
      const calculatedDimensions = calculateDimensions(childNodes);
      const shouldUseCalculated =
        childNodes.length > 0 &&
        (calculatedDimensions.width > initialDimensions.width ||
          calculatedDimensions.height > initialDimensions.height);

      if (shouldUseCalculated) {
        scheduleUpdate(calculatedDimensions);
      }
    }
  }, [dimensions, initialDimensions, calculateDimensions, childNodes, scheduleUpdate]);

  // Apply dimensions to React Flow node using proper React Flow patterns
  useEffect(() => {
    if (!dimensions) return;

    // Check if dimensions actually changed to prevent infinite loops
    const prevDims = previousDimensionsRef.current;
    if (prevDims && prevDims.width === dimensions.width && prevDims.height === dimensions.height) {
      return;
    }

    previousDimensionsRef.current = dimensions;

    setNodes(currentNodes =>
      currentNodes.map(node =>
        node.id === nodeId
          ? {
              ...node,
              width: dimensions.width,
              height: dimensions.height,
              style: {
                ...node.style,
                width: dimensions.width,
                height: dimensions.height,
              },
            }
          : node
      )
    );

    // Notify React Flow that node internals have changed
    updateNodeInternals(nodeId);
  }, [dimensions, nodeId, setNodes, updateNodeInternals]);

  return {
    dimensions,
    childNodes,
    isValid: !hasValidationErrors(nodeId),
    hasChildren: childNodes.length > 0,
  };
};
